import { useState, useCallback, useMemo } from 'react';
import usePenaltyStatus from '@/hooks/usePenaltyStatus';
import useAuthStore from '@/stores/authStore';
import { USER_STATUS, getUserStatus } from '@/utils/userStatus';
import { openPenaltyPaymentModal } from '@/stores/penaltyPaymentStore';

const usePenaltyGuard = (options = {}) => {
  const { onSecondaryAction } = options;
  const user = useAuthStore((state) => state.user);
  const {
    hasOutstandingPenalties,
    isBanned,
    isLoading: isPenaltyStatusLoading,
    isFetching: isPenaltyStatusFetching,
    isFetched: isPenaltyStatusFetched,
  } = usePenaltyStatus();
  const [dialogMode, setDialogMode] = useState(null);

  const status = useMemo(() => getUserStatus({ user, hasOutstandingPenalties }), [user, hasOutstandingPenalties]);

  const ensureActionAllowed = useCallback(() => {
    if (status === USER_STATUS.BANNED) {
      setDialogMode('banned');
      return false;
    }
    if (status === USER_STATUS.PENALTY_OUTSTANDING) {
      setDialogMode('penalty');
      return false;
    }
    return true;
  }, [status]);

  const closeDialog = useCallback(() => {
    setDialogMode(null);
  }, []);

  const goToPenaltyPayment = useCallback(() => {
    closeDialog();
    openPenaltyPaymentModal();
  }, [closeDialog]);

  const handleSecondary = useCallback(() => {
    closeDialog();
    if (typeof onSecondaryAction === 'function') {
      onSecondaryAction();
    }
  }, [closeDialog, onSecondaryAction]);

  return {
    ensureActionAllowed,
    status,
    hasOutstandingPenalties,
    isBanned,
    penaltyDialogProps: {
      isOpen: dialogMode !== null,
      mode: dialogMode === 'banned' ? 'banned' : 'penalty',
      onConfirm: goToPenaltyPayment,
      onSecondary: handleSecondary,
      onClose: closeDialog,
    },
    isPenaltyStatusLoading: isPenaltyStatusLoading || isPenaltyStatusFetching,
    isPenaltyStatusFetched,
  };
};

export default usePenaltyGuard;
